<template>
  <div>
    <template v-if="!isListPending">
      <template v-if="statements.length">
        <div class="d-none d-sm-block">
          <base-table borderless>
            <thead>
              <tr>
                <th>Date</th>
                <th>Transaction</th>
                <th>Amount</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(statement, key) in statements" :key="key">
                <td>
                  {{ $dates.toDayMonth(statement.transactionAt) }}
                  <br>
                  <small class="text-muted">
                    {{ $dates.toYear(statement.transactionAt) }}
                  </small>
                </td>
                <td>
                  <template v-if="statement.type">
                    {{ type(statement) }}
                    <br>
                    <small class="text-muted">
                      <router-link :to="route(statement)">
                        {{ detail(statement) }}
                      </router-link>
                    </small>
                  </template>
                  <template v-else>
                    No info available
                  </template>
                </td>
                <td>
                  <span :class="{
                    'text-success': !debits(statement),
                    'text-danger': debits(statement)
                  }">
                    {{ $formatting.toCurrency(statement.amount) }}
                  </span>
                  <br>
                  <small class="text-muted">
                    {{ debits(statement) ? 'Funds out' : 'Funds in' }}
                  </small>
                </td>
                <td>
                  {{ $formatting.toCurrency(statement.balanceAfter) }}
                  <br>
                  <small class="text-muted">Balance</small>
                </td>
              </tr>
            </tbody>
          </base-table>
        </div>
        <div class="d-sm-none">
          <template v-for="(statement, key) in statements">
            <base-table :key="key">
              <colgroup>
                <col width="50%" />
                <col width="50%" />
              </colgroup>
              <tbody>
                <tr>
                  <th>Date</th>
                  <td>
                    {{ $dates.toDayMonth(statement.transactionAt) }}
                  </td>
                </tr>
                <tr>
                  <th>Transaction</th>
                  <td>
                    <template v-if="statement.type">
                      {{ type(statement) }}
                    </template>
                    <template v-else>
                      No info available
                    </template>
                  </td>
                </tr>
                <tr>
                  <th>Amount</th>
                  <td>
                    <span :class="{
                      'text-success': !debits(statement),
                      'text-danger': debits(statement)
                    }">
                      {{ $formatting.toCurrency(statement.amount) }}
                    </span>
                  </td>
                </tr>
                <tr>
                  <th>Balance</th>
                  <td>
                    {{ $formatting.toCurrency(statement.balanceAfter) }}
                  </td>
                </tr>
                <tr>
                  <td colspan="2">
                    <router-link :to="route(statement)">
                      {{ detail(statement) }}
                    </router-link>
                  </td>
                </tr>
              </tbody>
            </base-table>
          </template>
        </div>
        <template v-if="statements.length && pages > 1">
          <base-pagination class="mb-3 mb-sm-0 mt-3" :current="page" :total="pages" @go="request" />
        </template>
      </template>
      <template v-else>
        <p>
          You don't have any statements yet.
        </p>
      </template>
    </template>
    <template v-else>
      <base-loader />
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import paymentTypes from '@/data/paymentTypes'

export default {
  name: 'AccountStatement',

  props: {
    account: {
      required: true,
      type: Object
    }
  },

  data: () => ({
    page: 1
  }),

  created () {
    this.request()
  },

  computed: {
    ...mapState({
      isListPending: store => store.core.statements.isListPending,
      pages: store => store.core.statements.totalPages,
      statements: store => store.core.statements.rows
    })
  },

  methods: {
    getData (statement) {
      const { account } = this
      const { investment } = statement.transaction || {}

      return { account, investment, statement }
    },

    getType (statement) {
      const key = this.$_.isPlainObject(statement.type) ? statement.type.key : statement.type

      return paymentTypes[key]
    },

    debits (statement) {
      return statement.balanceBefore > statement.balanceAfter
    },

    detail (statement) {
      const type = this.getType(statement)
      let suffix = ''

      if (this.$_.get(statement, 'transaction.investment.investmentType') === 'Portfolio') {
        suffix = 'Portfolio'
      }

      if (type) {
        return this.$_.trim(`${type.detailed(this.getData(statement))} ${suffix}`)
      }

      if (this.$_.has(statement, 'transaction') && this.$_.isPlainObject(statement.transaction.investment)) {
        return this.$_.trim(`Relating to ${statement.transaction.investment.title} ${suffix}`)
      }
    },

    request (page) {
      if (page) {
        this.page = page
      }

      this.$store.dispatch('core/statements/list', {
        page: this.page,
        query: {
          account: this.account._id
        },
        sort: '-transactionAt'
      })
    },

    route (statement) {
      if (this.$_.has(statement, 'transaction') && this.$_.isPlainObject(statement.transaction.investment)) {
        return this.$investment.getRoute(statement.transaction.investment)
      }

      const { name, params } = this.getType(statement).route
      const data = this.getData(statement)
      const formatted = {}

      this.$_.forIn(params, (value, key) => {
        formatted[key] = this.$_.get(data, value)
      })

      return { name, params: formatted }
    },

    type (statement) {
      if (this.$_.isPlainObject(statement.type)) {
        return statement.type.description
      }

      return this.getType(statement).description
    }
  }
}
</script>
